import { STORAGE_BACKENDS, STORAGE_NAMESPACE } from './storageKeys';

const keySubscribers = new Map();
const domainSubscribers = new Map();
let hasStorageListener = false;

const isBrowser = typeof window !== 'undefined';

const getStorage = (backend) => {
    if (!isBrowser) {
        return null;
    }

    if (backend === STORAGE_BACKENDS.LOCAL) {
        return window.localStorage;
    }

    if (backend === STORAGE_BACKENDS.SESSION) {
        return window.sessionStorage;
    }

    return null;
};

const getDomainForKey = (key) => {
    if (!key || !key.startsWith(`${STORAGE_NAMESPACE}:`)) {
        return null;
    }

    const parts = key.split(':');
    return parts.length >= 4 ? parts[2] : null;
};

const notifySubscribers = (event) => {
    const keySet = keySubscribers.get(event.key);
    if (keySet) {
        keySet.forEach((listener) => listener(event));
    }

    if (event.domain) {
        const domainSet = domainSubscribers.get(event.domain);
        if (domainSet) {
            domainSet.forEach((listener) => listener(event));
        }
    }
};

const attachStorageListener = () => {
    if (!isBrowser || hasStorageListener) {
        return;
    }

    window.addEventListener('storage', (event) => {
        if (!event.key) {
            return;
        }

        const domain = getDomainForKey(event.key);
        notifySubscribers({
            key: event.key,
            domain,
            backend: event.storageArea === window.sessionStorage ? STORAGE_BACKENDS.SESSION : STORAGE_BACKENDS.LOCAL,
            source: 'cross-tab',
            action: event.newValue === null ? 'remove' : 'set'
        });
    });

    hasStorageListener = true;
};

const emitLocalEvent = ({ key, domain, backend, action }) => {
    notifySubscribers({
        key,
        domain,
        backend,
        source: 'same-tab',
        action
    });
};

const safeJsonParse = (rawValue, fallbackValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
        return fallbackValue;
    }

    try {
        return JSON.parse(rawValue);
    } catch (error) {
        return fallbackValue;
    }
};

const readRaw = (key, backend) => {
    const storage = getStorage(backend);
    if (!storage) {
        return null;
    }

    try {
        return storage.getItem(key);
    } catch (error) {
        return null;
    }
};

const writeRaw = (key, value, backend, domain) => {
    const storage = getStorage(backend);
    if (!storage) {
        return;
    }

    try {
        storage.setItem(key, value);
        emitLocalEvent({ key, domain, backend, action: 'set' });
    } catch (error) {
        console.error('Could not write to storage', error);
    }
};

const removeRaw = (key, backend, domain) => {
    const storage = getStorage(backend);
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(key);
        emitLocalEvent({ key, domain, backend, action: 'remove' });
    } catch (error) {
        console.error('Could not remove storage key', error);
    }
};

const getJSON = (storageKey, fallbackValue = null) => {
    return safeJsonParse(readRaw(storageKey.key, storageKey.backend), fallbackValue);
};

const setJSON = (storageKey, value) => {
    writeRaw(storageKey.key, JSON.stringify(value), storageKey.backend, storageKey.domain);
};

const removeKey = (storageKey) => {
    removeRaw(storageKey.key, storageKey.backend, storageKey.domain);
};

const clearDomain = (domain, backend) => {
    const storage = getStorage(backend);
    if (!storage) {
        return;
    }

    const keysToRemove = [];
    for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key && key.startsWith(`${STORAGE_NAMESPACE}:${domain}:`)) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach((key) => removeRaw(key, backend, domain));
};

const subscribeToKey = (key, callback) => {
    attachStorageListener();

    const listeners = keySubscribers.get(key) || new Set();
    listeners.add(callback);
    keySubscribers.set(key, listeners);

    return () => {
        const activeListeners = keySubscribers.get(key);
        if (!activeListeners) {
            return;
        }

        activeListeners.delete(callback);
        if (activeListeners.size === 0) {
            keySubscribers.delete(key);
        }
    };
};

const subscribeToDomain = (domain, callback) => {
    attachStorageListener();

    const listeners = domainSubscribers.get(domain) || new Set();
    listeners.add(callback);
    domainSubscribers.set(domain, listeners);

    return () => {
        const activeListeners = domainSubscribers.get(domain);
        if (!activeListeners) {
            return;
        }

        activeListeners.delete(callback);
        if (activeListeners.size === 0) {
            domainSubscribers.delete(domain);
        }
    };
};

const storageService = {
    getJSON,
    setJSON,
    removeKey,
    clearDomain,
    readRaw,
    writeRaw,
    removeRaw,
    subscribeToKey,
    subscribeToDomain,
    safeJsonParse
};

export default storageService;
